import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Undo2, Warehouse } from 'lucide-react'
import { api, errorMessage } from '@/lib/api'
import type { Order, Partner } from '@/types'
import Button from '@/components/ui/Button'
import { Field } from '@/components/ui/Field'
import { Select } from '@/components/ui/Select'
import Modal from '@/components/ui/Modal'
import { useToast } from '@/components/ui/toast'

/**
 * Hands an order's job to an external warehouse partner, or takes it back.
 * The whole job goes: the partner picks up at the current section and the
 * in-house floor stops seeing it until it is taken back.
 */
export default function PartnerModal({ order, onClose }: { order: Order | null; onClose: () => void }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [warehouseId, setWarehouseId] = useState('')

  // Preselect the current partner so "Save" without a change is harmless.
  useEffect(() => {
    setWarehouseId(order?.job?.warehouse ? String(order.job.warehouse.id) : '')
  }, [order])

  const partners = useQuery({
    queryKey: ['admin-partners'],
    queryFn: async () => (await api.get<{ data: Partner[] }>('/admin/partners')).data.data,
    enabled: Boolean(order),
  })

  const save = useMutation({
    mutationFn: async (id: number | null) =>
      (await api.patch(`/admin/orders/${order!.id}/partner`, { warehouse_id: id })).data,
    onSuccess: (res: { message?: string }) => {
      queryClient.invalidateQueries({ queryKey: ['order-detail'] })
      queryClient.invalidateQueries({ queryKey: ['orders-page'] })
      queryClient.invalidateQueries({ queryKey: ['admin-jobs'] })
      onClose()
      toast.success(res.message ?? 'Saved')
    },
    onError: (err) => toast.error('Could not update the partner', errorMessage(err)),
  })

  // Only partners with a working login can act on a job.
  const options = (partners.data ?? [])
    .filter((p) => p.status === 'approved' || String(p.id) === warehouseId)
    .map((p) => ({ value: String(p.id), label: p.code ? `${p.name} (${p.code})` : p.name }))

  const current = order?.job?.warehouse
  // What the job will read as once the chosen partner holds it. The server's
  // displayCode() is the real rule; this only previews it.
  const chosenCode = partners.data?.find((p) => String(p.id) === warehouseId)?.code
  const preview = chosenCode && order?.job?.code ? order.job.code.replace(/^MPG(?=-)/, chosenCode) : null

  return (
    <Modal
      open={Boolean(order)}
      onClose={onClose}
      title={order ? `Warehouse partner - ${order.number}` : ''}
      footer={
        <>
          {current && (
            <Button
              variant="outline"
              className="sm:mr-auto"
              loading={save.isPending && save.variables === null}
              onClick={() => save.mutate(null)}
            >
              <Undo2 className="size-4" /> Take back in-house
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={save.isPending && save.variables !== null}
            disabled={!warehouseId || String(current?.id ?? '') === warehouseId}
            onClick={() => save.mutate(Number(warehouseId))}
          >
            <Warehouse className="size-4" /> {current ? 'Change partner' : 'Hand over'}
          </Button>
        </>
      }
    >
      {order && (
        <div className="space-y-3">
          <Field label="External warehouse partner">
            <Select
              value={warehouseId}
              onChange={setWarehouseId}
              placeholder={partners.isLoading ? 'Loading partners...' : 'Choose a partner...'}
              options={options}
            />
          </Field>
          {preview && (
            <p className="text-xs text-foreground">
              The job will show as <span className="font-mono font-bold">{preview}</span>.
            </p>
          )}
          {options.length === 0 && !partners.isLoading && (
            <p className="text-xs text-status-danger">
              No active partner yet. Add one under Warehouse Partners first.
            </p>
          )}
          <p className="text-xs text-muted">
            The partner picks up at the job's current section
            {order.job?.current_department ? ` (${order.job.current_department.name})` : ''} and updates every
            step from their own login. Your floor stops seeing the job until you take it back.
          </p>
        </div>
      )}
    </Modal>
  )
}
